import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AuthService } from "../../../common/auth/src/services/auth.service";
import { LoggedUserService } from "../../../common/auth/src/services/logged-user.service";
import { AuthFlowService } from "../../../services/auth-flow/auth-flow.service";
import { IntercomService } from "../../intercom.service";
import { PageLoaderService } from "../page-loader.service";
import { LogoutStateService } from "../../state/logout-state.service";
import { PaymentModalManagerService } from "../payment-modal-manager.service";
import { firstValueFrom } from "rxjs";

/**
 * Centralized service for handling user logout across the application.
 * Coordinates cleanup of all storage and service states.
 */
@Injectable({
  providedIn: "root",
})
export class CentralizedLogoutService {
  private readonly _router = inject(Router);
  private readonly _authService = inject(AuthService);
  private readonly _loggedUserService = inject(LoggedUserService);
  private readonly _authFlowService = inject(AuthFlowService);
  private readonly _intercomService = inject(IntercomService);
  private readonly _pageLoaderService = inject(PageLoaderService);
  private readonly _logoutStateService = inject(LogoutStateService);
  private readonly _paymentModalManager = inject(PaymentModalManagerService);

  /**
   * Storage keys to be cleared from localStorage
   */
  private readonly _localStorageKeys = [
    "core-user", // Keep for backward compatibility during migration
    "user-role", // Keep for backward compatibility during migration
    "auth-flow-user-data", // New encrypted user data
    "auth-flow-user-role", // New encrypted user role
    "cognito_access_token",
    "auth-flow-state", // <-- Keep this key to preserve auth flow state
  ];

  /**
   * Storage keys to be cleared from sessionStorage
   */
  private readonly _sessionStorageKeys = [
    // Old keys (keep for backward compatibility during migration)
    "registration-questionnaire-data",
    "registration-selected-doctor",
    "registration-selected-psychologist",
    "registration-selected-clinic",
    "registration-pathway-data",
    "registration-visit-id",
    "registration-completed",
    "first-booking-consultation",
    // New auth-flow keys
    "auth-flow-questionnaire-data",
    "auth-flow-questionnaire-completed",
    "auth-flow-selected-doctor",
    "auth-flow-selected-psycho",
    "auth-flow-selected-clinic",
    "auth-flow-pathway-data",
    "auth-flow-visit-id",
    "auth-flow-first-booking-completed",
    "auth-flow-flow-completed",
    "auth-flow-status-override",
  ];

  /**
   * Performs a complete logout operation:
   * 1. Clears all localStorage data
   * 2. Clears all sessionStorage data
   * 3. Resets service states
   * 4. Logs out from Cognito using AuthService
   * 5. Redirects to login page
   */
  public async logout(): Promise<void> {
    try {
      // Signal immediately that logout is in progress
      // This will hide router-outlet in AppComponent, destroying all active components
      this._logoutStateService.setLoggingOut(true);
      
      // Show loading overlay immediately
      this._pageLoaderService.show();
      
      // Small delay to ensure Angular has completed component destruction
      await new Promise(resolve => setTimeout(resolve, 100));

      // Now it's safe to clear all data since components are destroyed
      // Clear localStorage
      this._clearLocalStorage();

      // Clear sessionStorage
      this._clearSessionStorage();

      // Reset service states
      this._resetServiceStates();

      // Clear any auth cookies (if applicable)
      this._clearAuthCookies();

      // Wait 1 second to show the loading, then perform logout
      setTimeout(() => {
        // Perform Cognito logout using AuthService (this handles navigation and reload)
        this._authService.logout();
      }, 1000);
    } catch (error) {
      console.error("Error during logout:", error);
      // Reset logout state on error
      this._logoutStateService.setLoggingOut(false);
      // Hide loader and force cleanup
      this._pageLoaderService.forceHide();
      this._forceCleanup();
    }
  }

  /**
   * Clears all relevant localStorage keys
   */
  private _clearLocalStorage(): void {
    this._localStorageKeys.forEach((key) => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.error(`Failed to remove localStorage key ${key}:`, error);
      }
    });
  }

  /**
   * Clears all relevant sessionStorage keys
   */
  private _clearSessionStorage(): void {
    this._sessionStorageKeys.forEach((key) => {
      try {
        sessionStorage.removeItem(key);
      } catch (error) {
        console.error(`Failed to remove sessionStorage key ${key}:`, error);
      }
    });
  }

  /**
   * Resets states in all relevant services
   */
  private _resetServiceStates(): void {
    try {
      // Reset logged user service state
      this._loggedUserService.clearUser();
    } catch (error) {
      console.error("Failed to reset logged user service:", error);
    }

    try {
      // Clear registration flow data
      this._authFlowService.resetFlow();
    } catch (error) {
      console.error("Failed to clear registration flow:", error);
    }

    try {
      // Clear consultation service data
      sessionStorage.removeItem("first-booking-consultation");
    } catch (error) {
      console.error("Failed to clear consultation data:", error);
    }

    try {
      // Shutdown Intercom
      this._intercomService.shutdown();
    } catch (error) {
      console.error("Failed to shutdown Intercom:", error);
    }

    try {
      // Clear payment modal flags to ensure modal reappears after login
      this._paymentModalManager.clearAllFlags();
    } catch (error) {
      console.error("Failed to clear payment modal flags:", error);
    }
  }

  /**
   * Attempts to clear authentication cookies
   */
  private _clearAuthCookies(): void {
    // Clear cookies by setting them to expire in the past
    const cookiesToClear = ["auth_token", "refresh_token", "session_id"];
    cookiesToClear.forEach((cookieName) => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${window.location.hostname}`;
    });
  }

  /**
   * Force cleanup in case of errors - clears all storage
   */
  private _forceCleanup(): void {
    try {
      // Clear all localStorage
      localStorage.clear();

      // Clear all sessionStorage
      sessionStorage.clear();

      // Navigate to login
      this._router.navigate(["/"]).then(() => {
        window.location.reload();
      });
    } catch (error) {
      console.error("Force cleanup failed:", error);
      // Last resort - just reload
      window.location.href = "/";
    }
  }

  /**
   * Checks if user is currently logged in
   */
  public async isLoggedIn(): Promise<boolean> {
    const hasLocalUser = localStorage.getItem("core-user") !== null;
    try {
      const isAuthenticated = await firstValueFrom(
        this._authService.isAuthenticated$()
      );
      return isAuthenticated && hasLocalUser;
    } catch (error) {
      return false;
    }
  }

  /**
   * Gets current storage state for debugging
   */
  public getStorageState(): {
    localStorage: Record<string, any>;
    sessionStorage: Record<string, any>;
  } {
    const localStorageState: Record<string, any> = {};
    const sessionStorageState: Record<string, any> = {};

    this._localStorageKeys.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) {
        localStorageState[key] = value;
      }
    });

    this._sessionStorageKeys.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (value) {
        sessionStorageState[key] = value;
      }
    });

    return {
      localStorage: localStorageState,
      sessionStorage: sessionStorageState,
    };
  }
}